/* istanbul ignore file */
import React, { useEffect, useState } from 'react';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import useCrmService from 'domain/sales/salesService';
import PresaleCoordinatorDropdown from 'domain/sales/opportunity/presale/output/shared/input/PresaleCoordinatorDropdown';
import { useFormContext } from 'framework/form';
import { InputLoader } from 'framework/ui/mui-builder';

interface PresaleCoordinatorInputProps {
  source: string;
  label: string;
  placeholder: string;
  isVisible?: boolean | undefined;
}

interface PresaleCoordinator {
  alias: string;
}

const getInitialAliasByCurrentUser = (
  currentUserAlias: Nullable<string>,
  coordinators: PresaleCoordinator[],
): Nullable<string> => {
  if (!currentUserAlias) {
    return null;
  }

  return coordinators.some(coordinator => coordinator.alias === currentUserAlias) ? currentUserAlias : null;
};

const PresaleCoordinatorInput = (props: PresaleCoordinatorInputProps) => {
  const { source } = props;

  const { getUserAlias } = useAuthenticatedUserService();
  const { data, setFormData, isDirty } = useFormContext();
  const { getActivePresaleCoordinators } = useCrmService();

  const [coordinators, setCoordinators] = useState<PresaleCoordinator[]>([]);
  const [currentUserAlias, setCurrentUserAlias] = useState<Nullable<string>>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [currentAlias, activeCoordinators] = await Promise.all([getUserAlias(), getActivePresaleCoordinators()]);

        setCurrentUserAlias(currentAlias);
        setCoordinators(activeCoordinators);
      } catch (e) {
        setCurrentUserAlias(null);
        setCoordinators([]);
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const coordinatorFromForm = data?.[source];

  const initialCoordinatorAlias = coordinatorFromForm ?? getInitialAliasByCurrentUser(currentUserAlias, coordinators);

  useEffect(() => {
    if (!setFormData || isDirty || isLoading) {
      return;
    }

    if (!initialCoordinatorAlias) {
      return;
    }

    if (data?.[source] === initialCoordinatorAlias) {
      return;
    }

    setFormData({
      ...data,
      [source]: initialCoordinatorAlias,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCoordinatorAlias, source, isLoading]);

  if (isLoading) {
    return <InputLoader />;
  }
  return (
    <PresaleCoordinatorDropdown
      {...props}
      coordinators={coordinators}
      initialCoordinatorAlias={initialCoordinatorAlias}
    />
  );
};

export default PresaleCoordinatorInput;
